import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { PressKit } from '@modules/content-api/adapter/press-kit.adapter';
import { PressKitComponent } from '@modules/press-kit/press-kit.component';
import { RichSnippetFactory } from '@modules/rich-snippet/factories/rich-snippet.factory';
import { JsonLdService } from '@modules/rich-snippet/services/json-ld.service';
import { DataLayerService } from '@modules/shared/services/data-layer.service';
import { MetaTagsService } from '@modules/shared/services/meta-tags.service';
import { ActionIcons, ButtonItem } from '@modules/ui/action-container/action-container.component';

@Component({
  selector: 'mb-press-kit-page',
  templateUrl: './press-kit-page.component.html',
  styleUrls: ['./press-kit-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, PressKitComponent],
})
export class PressKitPageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private dataLayerService = inject(DataLayerService);
  private metaTagsService = inject(MetaTagsService);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private jsonLdService = inject(JsonLdService);
  private richSnippetFactory = inject(RichSnippetFactory);

  public pressKit!: PressKit;
  public defaultActions: ButtonItem[] = [
    {
      icon: ActionIcons.SHARE,
      class: 'btn btn-plain d-none d-sm-inline-flex',
    },
    {
      icon: ActionIcons.DOWNLOAD,
      class: 'btn btn-plain d-none d-sm-inline-flex',
    },
    {
      icon: ActionIcons.BOOKMARK,
      class: 'btn btn-plain',
    },
  ];
  public sectionActions: ButtonItem[] = [
    {
      icon: ActionIcons.DOWNLOAD,
      label: 'DOWNLOAD',
      class: 'btn btn-plain',
    },
  ];
  public pressKitActions: ButtonItem[] = [
    {
      icon: ActionIcons.SHARE,
      label: 'SHARE',
      class: 'btn btn-plain',
    },
    {
      icon: ActionIcons.DOWNLOAD,
      label: 'DOWNLOAD',
      class: 'btn btn-plain',
    },
    {
      icon: ActionIcons.BOOKMARK,
      label: 'SAVE',
      class: 'btn btn-plain',
    },
  ];

  private destroy$ = new Subject<boolean>();

  ngOnInit(): void {
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.pressKit) {
        this.pressKit = data.pressKit;
        this.cdr.detectChanges();
        this.setPressKitMetaTags();

        if (isPlatformBrowser(this.platformId)) {
          this.pushPageViewDataLayer();
        }

        const richSnippet = this.richSnippetFactory.create('presskit').adapt(this.pressKit);
        this.jsonLdService.addJsonLd(richSnippet, `presskit-${this.pressKit.id}`);
      }
    });
  }

  private setPressKitMetaTags(): void {
    if (!this.pressKit) return;

    const pressKitUrl = this.document.location.origin + this.document.location.pathname;
    const imageUrl = this.pressKit.mainImage?.original?.url || undefined;
    const description = this.metaTagsService.extractDescription(this.pressKit.content, 200);

    this.metaTagsService.setArticleMetaTags({
      title: this.pressKit.title,
      url: pressKitUrl,
      image: imageUrl,
      description: description,
    });
  }

  private pushPageViewDataLayer() {
    this.dataLayerService.pushPageView({
      id: this.pressKit.id,
      type: 'press_kit',
      name: this.pressKit.title,
      publishDate: this.pressKit.displayDate,
      marsOriginId: this.pressKit.marsOriginId,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.metaTagsService.removeArticleMetaTags();

    if (this.pressKit) {
      this.jsonLdService.removeJsonLd(`presskit-${this.pressKit.id}`);
    }
  }
}
